import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { History, Loader2, Lock, LockOpen, Users2, CalendarDays } from "lucide-react";
import { api } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import { useAuth } from "@/context/AuthContext";
import { usePermissions } from "@/lib/permissions";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { fmtDate } from "@/lib/format";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { getErrorMessage } from "@/lib/errors";
import { cn } from "@/lib/utils";
import { HideWhenSingleCompany } from "@/components/CompanyField";
import { BRANCH_KEY } from "@/lib/api";

const readBranch = () => { try { return localStorage.getItem(BRANCH_KEY) || ""; } catch { return ""; } };

const STATUS = {
  PRESENT: { label: "P", full: "Present", cls: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400" },
  HALF_DAY: { label: "½", full: "Half day", cls: "bg-amber-500/15 text-amber-700 dark:text-amber-400" },
  ABSENT: { label: "A", full: "Absent", cls: "bg-rose-500/15 text-rose-700 dark:text-rose-400" },
  WEEKLY_OFF: { label: "WO", full: "Weekly off (paid)", cls: "bg-sky-500/15 text-sky-700 dark:text-sky-400" },
  HOLIDAY: { label: "H", full: "Holiday (paid)", cls: "bg-violet-500/15 text-violet-700 dark:text-violet-400" },
  PAID_LEAVE: { label: "PL", full: "Paid leave", cls: "bg-teal-500/15 text-teal-700 dark:text-teal-400" },
  UNPAID_LEAVE: { label: "LOP", full: "Unpaid leave (loss of pay)", cls: "bg-muted text-muted-foreground" },
};
const CYCLE = ["PRESENT", "HALF_DAY", "ABSENT", "WEEKLY_OFF", "HOLIDAY", "PAID_LEAVE", "UNPAID_LEAVE"];
const thisMonth = () => new Date().toISOString().slice(0, 7);

/** The browser's guess at the markable days until the server answers (today and the 3 days before, local time). */
function localWindow() {
  const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const today = new Date();
  const first = new Date(today);
  first.setDate(today.getDate() - 3);
  return { first: iso(first), last: iso(today), days_back: 3 };
}

export default function Attendance() {
  const { companies } = useCompany() || {};
  const [companyId, setCompanyId] = useState("");
  const [month, setMonth] = useState(thisMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState({}); // key "empId-day" -> true while saving
  const [picker, setPicker] = useState(null); // { empId, day } for the status menu
  const { isOrgAdmin } = useAuth() || {};
  const { can } = usePermissions();
  const [locks, setLocks] = useState([]);
  const [changing, setChanging] = useState(null);   // a correction waiting for its reason: { empId, day, status, from }
  const [unlocking, setUnlocking] = useState(false);
  const [history, setHistory] = useState(null);
  // With a branch chosen, the server lists only the locks that apply to it. With "All branches", a lock filed under one branch closes only that
  // branch's people: the sheet stays open and the server refuses their days; the banner says which branches are closed.
  const branchChosen = !!readBranch();
  const monthLocks = locks.filter((l) => l.scope === "ATTENDANCE" && l.period === `${month}-01`);
  const lock = monthLocks.find((l) => !l.branch_id || branchChosen);
  const partLocks = lock ? [] : monthLocks;
  // Days that can be marked by hand: today and the past 3 (the server says which, in the organisation's time zone; refused there too).
  const [win, setWin] = useState(() => localWindow());
  useEffect(() => { api.get("/attendance/window").then(({ data: w }) => w?.first && w?.last && setWin(w)).catch(() => {}); }, []);
  const dayIso = (d) => `${month}-${String(d).padStart(2, "0")}`;
  const markable = (d) => dayIso(d) >= win.first && dayIso(d) <= win.last;

  const loadLocks = async () => { try { setLocks((await api.get("/hr/period-locks")).data); } catch { /* the page works without it */ } };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadLocks(); }, []);

  useEffect(() => { if (!companyId && companies?.[0]) setCompanyId(companies[0].id); }, [companies, companyId]);

  const load = async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get("/attendance/month", { params: { period: `${month}-01`, company_id: companyId || undefined } });
      setData(res);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [month, companyId]);

  const days = useMemo(() => (data ? Array.from({ length: data.days_in_month }, (_, i) => i + 1) : []), [data]);
  const weekday = (d) => new Date(`${month}-${String(d).padStart(2, "0")}T00:00:00`).toLocaleDateString("en-IN", { weekday: "short" })[0];
  const isWeekend = (d) => [0, 6].includes(new Date(`${month}-${String(d).padStart(2, "0")}T00:00:00`).getDay());

  // A day marked for the first time is saved at once. Changing a day that was already marked asks why, so the correction is on record.
  const setStatus = (empId, day, status) => {
    const from = data?.employees.find((e) => e.id === empId)?.days?.[day]?.status;
    if (from && from !== status) { setPicker(null); setChanging({ empId, day, status, from }); return; }
    return save(empId, day, status);
  };

  const save = async (empId, day, status, reason) => {
    const key = `${empId}-${day}`;
    setSaving((s) => ({ ...s, [key]: true }));
    try {
      await api.post("/attendance/mark", { employee_id: empId, attendance_date: `${month}-${String(day).padStart(2, "0")}`, status, reason: reason || undefined });
      setData((d) => ({
        ...d,
        employees: d.employees.map((e) => (e.id === empId ? { ...e, days: { ...e.days, [day]: { status, overtime_hours: "0" } } } : e)),
      }));
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving((s) => ({ ...s, [key]: false }));
      setPicker(null);
    }
  };

  const cycleStatus = (empId, day, current) => {
    const next = CYCLE[(CYCLE.indexOf(current || "PRESENT") + 1) % CYCLE.length];
    setStatus(empId, day, next);
  };

  const markTodayAll = async (status) => {
    if (!data?.employees?.length) return;
    const today = win.last;
    if (today.slice(0, 7) !== month) { toast.error("Switch to the current month to mark today for everyone"); return; }
    try {
      await api.post("/attendance/bulk-mark", { employee_ids: data.employees.map((e) => e.id), attendance_date: today, status });
      toast.success(`Marked ${STATUS[status].full.toLowerCase()} for everyone today`);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const lockMonth = async () => {
    try { await api.post("/hr/period-locks", { scope: "ATTENDANCE", period: `${month}-01` }); toast.success("Attendance for this month is locked"); loadLocks(); }
    catch (err) { toast.error(getErrorMessage(err)); }
  };
  const showHistory = async () => {
    try {
      const last = new Date(Number(month.slice(0, 4)), Number(month.slice(5, 7)), 0).getDate();
      setHistory((await api.get("/attendance/history", { params: { date_from: `${month}-01`, date_to: `${month}-${String(last).padStart(2, "0")}` } })).data);
    } catch (err) { toast.error(getErrorMessage(err)); }
  };

  const monthPaidDays = (emp) => Object.values(emp.days || {}).reduce((sum, d) => {
    const v = { PRESENT: 1, HALF_DAY: 0.5, WEEKLY_OFF: 1, HOLIDAY: 1, PAID_LEAVE: 1 }[d.status] ?? 0;
    return sum + v;
  }, 0);

  return (
    <div className="space-y-5" data-testid="attendance-page">
      <PageHeader
        title="Attendance"
        description="Mark each employee's attendance day by day, or bulk-mark today for the whole team. Payroll uses this month's paid days automatically — an employee with nothing marked is paid for the full month, as before."
        action={<div className="flex flex-wrap items-end gap-2">
          <HideWhenSingleCompany><Select value={companyId} onValueChange={setCompanyId}>
            <SelectTrigger className="w-48"><SelectValue placeholder="Company" /></SelectTrigger>
            <SelectContent>{(companies || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select></HideWhenSingleCompany>
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-40" />
          <Button size="sm" variant="outline" onClick={showHistory} data-testid="attendance-history-button"><History className="h-3.5 w-3.5" /> Changes</Button>
          {!lock && can("HR_PAYROLL", "approve") && <Button size="sm" variant="outline" onClick={lockMonth} data-testid="attendance-lock-button"><Lock className="h-3.5 w-3.5" /> Lock month</Button>}
        </div>}
      />

      {partLocks.length > 0 && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="attendance-part-locked">
          Locked for this month at: {partLocks.map((l) => l.branch || "a branch").join(", ")}. Days of people in {partLocks.length === 1 ? "that branch" : "those branches"} cannot be changed; choose the branch at the top to see or unlock it.</div>)}
      {lock && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="attendance-locked-banner">
          <Lock className="h-4 w-4" />
          <span>Attendance for this month is locked{lock.branch ? ` at ${lock.branch}` : ""}{lock.source === "PAYROLL_POST" ? " because its payroll was posted" : ""}{lock.locked_by ? ` (by ${lock.locked_by})` : ""}. Days cannot be marked or changed.</span>
          {isOrgAdmin && <Button size="sm" variant="outline" className="ml-auto" onClick={() => setUnlocking(true)} data-testid="attendance-unlock-button"><LockOpen className="h-3.5 w-3.5" /> Unlock</Button>}
        </div>)}
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-muted/30 p-2 text-xs">
        <span className="flex items-center gap-1 text-muted-foreground"><Users2 className="h-3.5 w-3.5" /> Mark everyone present today:</span>
        <Button size="sm" variant="outline" disabled={!!lock} onClick={() => markTodayAll("PRESENT")}>All Present</Button>
        <Button size="sm" variant="outline" disabled={!!lock} onClick={() => markTodayAll("WEEKLY_OFF")}>All Weekly Off</Button>
        <Button size="sm" variant="outline" disabled={!!lock} onClick={() => markTodayAll("HOLIDAY")}>All Holiday</Button>
        <span className="ml-auto flex flex-wrap gap-2">
          {Object.entries(STATUS).map(([k, v]) => <span key={k} className={cn("rounded px-1.5 py-0.5", v.cls)}>{v.label} {v.full}</span>)}
        </span>
      </div>
      <p className="text-xs text-muted-foreground" data-testid="attendance-window-note">
        Only today and the past {win.days_back ?? 3} days can be marked; other days are greyed out. Approved leave marks future days on its own.
      </p>

      <div className="overflow-x-auto rounded-xl border border-border bg-card">
        {loading && !data ? (
          <p className="flex items-center gap-2 p-6 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</p>
        ) : !data || data.employees.length === 0 ? (
          <p className="p-6 text-center text-sm text-muted-foreground">No active employees in this company. Add them on the Employees page.</p>
        ) : (
          <table className="w-full border-collapse text-xs">
            <thead>
              <tr className="border-b border-border bg-muted/40">
                <th className="sticky left-0 z-10 min-w-[11rem] bg-muted/40 px-3 py-2 text-left font-medium">Employee</th>
                {days.map((d) => (
                  <th key={d} className={cn("min-w-[30px] px-0.5 py-2 text-center font-normal", isWeekend(d) && "text-muted-foreground")}>
                    <div>{d}</div><div className="text-[9px]">{weekday(d)}</div>
                  </th>
                ))}
                <th className="min-w-[52px] px-2 py-2 text-right font-medium">Paid</th>
              </tr>
            </thead>
            <tbody>
              {data.employees.map((emp) => (
                <tr key={emp.id} className="border-b border-border/60" data-testid={`attendance-row-${emp.code}`}>
                  <td className="sticky left-0 z-10 bg-card px-3 py-1.5">
                    <div className="text-foreground">{emp.name}</div>
                    <div className="text-[10px] text-muted-foreground">{emp.code}</div>
                  </td>
                  {days.map((d) => {
                    const entry = emp.days[d];
                    const st = entry?.status;
                    const key = `${emp.id}-${d}`;
                    const open = markable(d);
                    return (
                      <td key={d} className="px-0.5 py-1 text-center">
                        <button
                          type="button"
                          disabled={saving[key] || !!lock || !open}
                          onClick={() => cycleStatus(emp.id, d, st)}
                          onContextMenu={(e) => { e.preventDefault(); if (open && !lock) setPicker({ empId: emp.id, day: d }); }}
                          title={!open ? `${st ? `${STATUS[st].full}. ` : ""}Only today and the past ${win.days_back ?? 3} days can be marked` : st ? STATUS[st].full : "Not marked — click to mark Present, right-click for options"}
                          data-testid={`attendance-cell-${emp.code}-${d}`}
                          className={cn("h-6 w-6 rounded text-[10px] font-medium transition hover:opacity-80",
                            st ? STATUS[st].cls : "border border-dashed border-border text-muted-foreground",
                            !open && "cursor-not-allowed opacity-40 hover:opacity-40")}
                        >
                          {saving[key] ? <Loader2 className="mx-auto h-3 w-3 animate-spin" /> : (st ? STATUS[st].label : "")}
                        </button>
                        {picker?.empId === emp.id && picker?.day === d && (
                          <div className="absolute z-20 mt-1 flex flex-col gap-0.5 rounded-md border border-border bg-popover p-1 shadow-lg">
                            {Object.entries(STATUS).map(([k, v]) => (
                              <button key={k} type="button" onClick={() => setStatus(emp.id, d, k)} className={cn("rounded px-2 py-1 text-left text-[10px] hover:bg-accent", v.cls)}>{v.full}</button>
                            ))}
                            <button type="button" onClick={() => setPicker(null)} className="rounded px-2 py-1 text-left text-[10px] text-muted-foreground hover:bg-accent">Cancel</button>
                          </div>
                        )}
                      </td>
                    );
                  })}
                  <td className="px-2 py-1.5 text-right font-mono text-foreground">{monthPaidDays(emp)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {changing && <CorrectionDialog change={changing} onClose={() => setChanging(null)} onSave={async (reason) => { const c = changing; setChanging(null); await save(c.empId, c.day, c.status, reason); }} />}
      {unlocking && <UnlockDialog month={month} onClose={() => setUnlocking(false)} onDone={() => { setUnlocking(false); loadLocks(); }} />}
      {history && (
        <Dialog open onOpenChange={(o) => !o && setHistory(null)}>
          <DialogContent className="max-h-[80vh] overflow-y-auto sm:max-w-2xl" data-testid="attendance-history-dialog">
            <DialogHeader><DialogTitle>Changes to marked days</DialogTitle><DialogDescription>Every time a day that was already marked was changed: what it was, what it became, who and why.</DialogDescription></DialogHeader>
            {history.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="attendance-history-empty">No marked day has been changed this month.</p> : (
              <ul className="divide-y divide-border text-sm" data-testid="attendance-history-list">{history.map((h) => (
                <li key={h.id} className="py-2"><span className="font-medium">{h.employee}</span> · {fmtDate(h.date)}: {STATUS[h.old?.status]?.full || "—"} → {h.new ? STATUS[h.new.status]?.full : "mark removed"}
                  <div className="text-xs text-muted-foreground">{h.kind === "LEAVE" ? "By leave" : h.kind === "CLOCK" ? "Clock" : "Correction"}{h.by ? ` · ${h.by}` : ""}{h.reason ? ` · ${h.reason}` : ""}</div></li>))}</ul>)}
          </DialogContent>
        </Dialog>)}
      <p className="flex items-center gap-1.5 text-xs text-muted-foreground"><CalendarDays className="h-3.5 w-3.5" /> Click a day to cycle Present → Half day → Absent → Weekly off → Holiday → Paid leave → Unpaid leave. Right-click (or long-press) a day for the full list.</p>
    </div>
  );
}

function CorrectionDialog({ change, onClose, onSave }) {
  const [reason, setReason] = useState("");
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="correction-dialog">
        <DialogHeader><DialogTitle>Change a marked day</DialogTitle><DialogDescription>{STATUS[change.from]?.full} → {STATUS[change.status]?.full}. The change is kept with your name.</DialogDescription></DialogHeader>
        <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why is it being changed? (recommended)" data-testid="correction-reason" />
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={() => onSave(reason.trim())} data-testid="correction-save">Change it</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UnlockDialog({ month, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const go = async () => {
    try { await api.post("/hr/period-locks/unlock", { scope: "ATTENDANCE", period: `${month}-01`, reason: reason.trim() }); toast.success("Unlocked"); onDone(); }
    catch (err) { toast.error(getErrorMessage(err)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="unlock-dialog">
        <DialogHeader><DialogTitle>Unlock attendance for this month</DialogTitle><DialogDescription>Say why. The reason is kept in the audit log.</DialogDescription></DialogHeader>
        <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="unlock-reason" />
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button disabled={reason.trim().length < 5} onClick={go} data-testid="unlock-submit">Unlock</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
